import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Archive, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PermanentDeleteDialog } from "@/components/drivers/PermanentDeleteDialog";

export function TerminateDriverDialog({ driver, onClose }) {
  const { user } = useAuth();
  const [status, setStatus] = useState("terminated");
  const [date, setDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [purging, setPurging] = useState(false);
  const canPurge = user?.role === "admin" || (user?.role === "customer" && !user?.shared);
  useEffect(() => { if (driver) { setStatus("terminated"); setDate(new Date().toISOString().slice(0, 10)); } }, [driver]);
  const submit = async () => {
    setBusy(true);
    try {
      await api.delete(`/drivers/${driver.id}`, { params: { status, terminated_at: date } });
      toast.success(`${driver.first_name} ${driver.last_name} moved to the retention folder`);
      onClose(true);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!driver} onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="sm:max-w-md" data-testid="terminate-driver-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Archive className="h-5 w-5 text-slate-600" />Separate {driver?.first_name} {driver?.last_name}</DialogTitle>
          <DialogDescription>Drivers are never deleted outright. This marks the driver as separated, removes them from the random pool and alerts, and files their records in the Retention folder. Records are disposed automatically once every FMCSA retention window (49 CFR 391.51 / 382.401) has passed.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="inline-flex rounded-full border border-slate-200 bg-white p-0.5 text-sm" role="group">
            {[["terminated", "Terminated"], ["self_terminated", "Self-terminated / quit"]].map(([v, l]) => (
              <button key={v} type="button" onClick={() => setStatus(v)} className={`px-3 py-1 rounded-full font-medium transition-colors ${status === v ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`} data-testid={`terminate-status-${v}`}>{l}</button>
            ))}
          </div>
          <div>
            <Label htmlFor="sep-date">Separation date</Label>
            <Input id="sep-date" type="date" value={date} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setDate(e.target.value)} data-testid="terminate-date-input" />
          </div>
        </div>
        <DialogFooter className="sm:justify-between gap-2">
          {canPurge ? <button type="button" onClick={() => setPurging(true)} className="text-xs text-red-600 underline inline-flex items-center gap-1 self-center" data-testid="terminate-purge-link"><Trash2 className="h-3 w-3" />Added by mistake? Delete permanently</button> : <span />}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onClose(false)} data-testid="terminate-cancel">Cancel</Button>
            <Button className="bg-slate-900 hover:bg-slate-800" disabled={busy || !date} onClick={submit} data-testid="terminate-confirm">Move to retention</Button>
          </div>
        </DialogFooter>
      </DialogContent>
      <PermanentDeleteDialog driver={driver} open={purging} onClose={(done) => { setPurging(false); if (done) onClose(true); }} />
    </Dialog>
  );
}
